import React, { useEffect, useRef } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./Services.css";

gsap.registerPlugin(ScrollTrigger);

const FlippingCards = () => {
  // Refs for animations
  const heroRef = useRef(null);
  const consultingRef = useRef(null);
  const developmentRef = useRef(null);
  const infrastructureRef = useRef(null);
  const academyRef = useRef(null);

  useEffect(() => {
    // Hero section animation
    gsap.fromTo(
      heroRef.current,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Animate each service section
    const sections = [consultingRef, developmentRef, infrastructureRef, academyRef];
    
    sections.forEach((ref) => {
      if (ref.current) {
        const cards = ref.current.querySelectorAll(".service-card");
        
        gsap.fromTo(
          cards,
          { y: 80, opacity: 0, scale: 0.9 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.8,
            stagger: 0.2,
            ease: "back.out(1.7)",
            scrollTrigger: {
              trigger: ref.current,
              start: "top 75%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    });
  }, []);

  return (
    <div className="services-wrapper" id="services">
      {/* Hero Section */}
      <section ref={heroRef} className="services-hero bg-light py-5">
        <Container className="text-center">
          {/* <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 mx-auto"
            style={{
              background: "linear-gradient(135deg, rgba(105, 54, 244, 0.1), rgba(139, 92, 246, 0.1))",
              border: "1px solid rgba(105, 54, 244, 0.2)",
              display: "inline-block"
            }}
          >
            <i className="fa-solid fa-briefcase text-[#6936F4]"></i>
            <span className="text-sm font-semibold" style={{ color: "#6936F4" }}>Our Services</span>
          </div> */}
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: "#0F0C1F" }}>
            Empowering Businesses Through Technology
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            From digital transformation to cutting-edge development, we deliver comprehensive ICT solutions 
            that drive innovation and growth.
          </p>
        </Container>
      </section>

      {/* Consulting Services - Dark Background */}
      <section ref={consultingRef} className="py-5" style={{ background: "linear-gradient(rgba(15,12,31,0.85), rgba(15,12,31,0.92)), url('https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1920&q=80') center/cover no-repeat" }}>
        <Container>
          <div className="text-center mb-5">
            <h3 className="gradient-text text-3xl md:text-4xl font-bold mb-3 text-blue-50">Consulting Services</h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Expert guidance to navigate the digital landscape and optimize your technology investments
            </p>
          </div>

          <Row className="g-4">
            {/* Digital Transformation */}
            <Col md={4}>
              <Card className="service-card h-full border-0 shadow-lg overflow-hidden">
                <div className="service-card-image" style={{ height: "200px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-full">
                    <i className="fa-solid fa-arrows-rotate text-white" style={{ fontSize: "4rem", strokeWidth: 2 }}></i>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-book text-white"></i>
                    </div>
                    <h4 className="mb-0 font-bold">Digital Transformation</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Leverage technology to fundamentally change how your business operates and delivers value.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Process automation & digitization</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Digital strategy development</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Employee training & adoption</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* Insights and Analytics */}
            <Col md={4}>
              <Card className="service-card h-full border-0 shadow-lg overflow-hidden">
                <div className="service-card-image" style={{ height: "200px", background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-full">
                    <i className="fa-solid fa-chart-line text-white" style={{ fontSize: "4rem" }}></i>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #f093fb, #f5576c)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-lightbulb text-white"></i>
                    </div>
                    <h4 className="mb-0 font-bold">Insights & Analytics</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Make informed decisions with data-driven insights and advanced analytics capabilities.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Data warehousing & integration</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Advanced analytics & BI</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Data visualization (Tableau, Power BI)</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* Cloud Services */}
            <Col md={4}>
              <Card className="service-card h-full border-0 shadow-lg overflow-hidden">
                <div className="service-card-image" style={{ height: "200px", background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-full">
                    <i className="fa-solid fa-cloud text-white" style={{ fontSize: "4rem" }}></i>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #4facfe, #00f2fe)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-server text-white"></i>
                    </div>
                    <h4 className="mb-0 font-bold">Cloud Services</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Optimize your cloud infrastructure for scalability, security, and cost-effectiveness.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Cloud migration & deployment</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Cloud security & management</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Infrastructure optimization</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Development & Products - Light Background */}
      <section ref={developmentRef} className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h3 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: "#0F0C1F" }}>Development & Products</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Cutting-edge solutions and innovative products tailored to your industry needs
            </p>
          </div>

          <Row className="g-4">
            {/* Software Development */}
            <Col md={4}>
              <Card className="service-card h-full border-0 shadow-lg overflow-hidden">
                <div className="service-card-image" style={{ height: "200px", background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-full">
                    <i className="fa-solid fa-laptop-code text-white" style={{ fontSize: "4rem" }}></i>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #fa709a, #fee140)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-laptop-code text-white"></i>
                    </div>
                    <h4 className="mb-0 font-bold">Software Development</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Custom software solutions that enhance efficiency and keep you competitive.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Mobile app development</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>API design & integration</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>IoT & AI solutions</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* E-Waste Management */}
            <Col md={4}>
              <Card className="service-card h-full border-0 shadow-lg overflow-hidden position-relative">
                <div className="position-absolute top-0 end-0 m-2 px-2 py-1 rounded" style={{ background: "#22c55e", color: "white", fontSize: "0.75rem", fontWeight: "bold" }}>
                  eBin Platform
                </div>
                <div className="service-card-image" style={{ height: "200px", background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-full">
                    <i className="fa-solid fa-recycle text-white" style={{ fontSize: "3.5rem", fontWeight: 900 }}></i>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #43e97b, #38f9d7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-leaf text-white"></i>
                    </div>
                    <h4 className="mb-0 font-bold">E-Waste Management</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    eBin ecosystem for responsible e-waste disposal, recycling, and circular economy.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Government & private sector integration</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Environmental impact reduction</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Economic value creation</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* System Design */}
            <Col md={4}>
              <Card className="service-card h-full border-0 shadow-lg overflow-hidden">
                <div className="service-card-image" style={{ height: "200px", background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-full">
                    <i className="fa-solid fa-sitemap text-white" style={{ fontSize: "4rem" }}></i>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #a8edea, #fed6e3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-pen-ruler text-white"></i>
                    </div>
                    <h4 className="mb-0 font-bold">System Design</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Innovative products tailored to specific industries using cutting-edge technology.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Extensive R&D and prototyping</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>White papers & documentation</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Industry-specific solutions</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Infrastructure Services - Dark Background */}
      <section ref={infrastructureRef} className="py-5" style={{ background: "linear-gradient(rgba(15,12,31,0.82), rgba(15,12,31,0.9)), url('https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1920&q=80') center/cover no-repeat" }}>
        <Container>
          <div className="text-center mb-5">
            <h3 className="gradient-text text-3xl md:text-4xl font-bold mb-3 text-blue-50">Infrastructure Services</h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Robust ICT infrastructure solutions for secure, scalable operations
            </p>
          </div>

          <Row className="g-4">
            {/* Network Design */}
            <Col md={4}>
              <Card className="service-card h-full border-0 shadow-lg overflow-hidden">
                <div className="service-card-image" style={{ height: "200px", background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-full">
                    <i className="fa-solid fa-network-wired text-white" style={{ fontSize: "4rem" }}></i>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #ff9a9e, #fecfef)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-diagram-project text-white"></i>
                    </div>
                    <h4 className="mb-0 font-bold">Network Design</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Reliable and secure network infrastructure tailored to your organizational needs.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Network assessment & planning</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Implementation & testing</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Ongoing monitoring & optimization</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* Server & Storage */}
            <Col md={4}>
              <Card className="service-card h-full border-0 shadow-lg overflow-hidden">
                <div className="service-card-image" style={{ height: "200px", background: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-full">
                    <i className="fa-solid fa-database text-white" style={{ fontSize: "4rem" }}></i>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #ffecd2, #fcb69f)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-hard-drive text-white"></i>
                    </div>
                    <h4 className="mb-0 font-bold">Server & Storage</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Efficient server and storage solutions with focus on performance and data protection.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Virtualization services</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Backup & disaster recovery</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Performance tuning</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* Cybersecurity */}
            <Col md={4}>
              <Card className="service-card h-full border-0 shadow-lg overflow-hidden">
                <div className="service-card-image" style={{ height: "200px", background: "linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-full">
                    <i className="fa-solid fa-shield text-white" style={{ fontSize: "4rem" }}></i>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #ff6e7f, #bfe9ff)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-lock text-white"></i>
                    </div>
                    <h4 className="mb-0 font-bold">Cybersecurity</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Comprehensive security solutions to protect your data and systems from threats.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Security assessment & design</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Compliance management</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Incident response & monitoring</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Intek Academy - Light Background */}
      <section ref={academyRef} className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h3 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: "#0F0C1F" }}>Intek Academy</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Research, innovation, and skills development for the future of technology
            </p>
          </div>

          <Row className="g-4 align-items-stretch">
            {/* Research & Innovation */}
            <Col md={6}>
              <Card className="service-card h-full border-0 shadow-lg">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-flask text-white" style={{ fontSize: "1.5rem" }}></i>
                    </div>
                    <h4 className="mb-0 font-bold">Research & Innovation</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Systematic, evidence-based approach from problem identification to solution dissemination.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Problem identification & research</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Prototype testing & refinement</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Solution implementation & monitoring</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            {/* Skills Development */}
            <Col md={6}>
              <Card className="service-card h-full border-0 shadow-lg">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-wrapper me-3" style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #f093fb, #f5576c)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <i className="fa-solid fa-graduation-cap text-white" style={{ fontSize: "1.5rem" }}></i>
                    </div>
                    <h4 className="mb-0 font-bold">Skills Development</h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Comprehensive ICT training programs including robotics and automation for sustainable tech adoption.
                  </p>
                  <ul className="list-unstyled small text-gray-600">
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Robotics & automation training</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Industry exposure & mentorship</li>
                    <li className="mb-2"><i className="fa-solid fa-check text-success me-2"></i>Career guidance & certification</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <Container className="text-center text-white">
          <h3 className="text-3xl font-bold mb-3">Ready to Transform Your Business?</h3>
          <p className="text-lg mb-4">
            Let's discuss how our services can drive your digital transformation journey
          </p>
          <a href="#contact1" className="btn btn-light btn-lg rounded-pill px-5 py-3">
            Get Started Today
          </a>
        </Container>
      </section>
    </div>
  );
};

export default FlippingCards;